import { useState, useRef, useEffect, useCallback } from 'react';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import ReplyIcon from '@mui/icons-material/Reply';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import DownloadFileButton from '../ui/DownloadFileButton/DownloadFileButton';
import useAuthContext from '../../contexts/Auth/useAuthContext';
import { postAuth } from '../../api/ApiCalls';
import { InquiryMessageDtoIn } from '../../models/in/InquiryDetailDtoIn';
import { SenderRole } from '../../models/common/InquiryEnums';
import styles from './ChatPanel.module.scss';

const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;

interface UploadedDoc { id: number; name: string }

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface ChatPanelProps {
  messages: InquiryMessageDtoIn[];
  onSendMessage: (content: string, isInternalNote: boolean, replyToId: number | null, documentIds: number[]) => void;
  isManagement: boolean;
  isClosed: boolean;
  isLoading?: boolean;
}

export default function ChatPanel({
  messages, onSendMessage, isManagement, isClosed, isLoading,
}: ChatPanelProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [input, setInput] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadError(null);

    const validFiles: File[] = [];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setUploadError(t('inquiries.chat.fileTooLarge', { name: file.name, max: MAX_FILE_SIZE_MB }));
        continue;
      }
      validFiles.push(file);
    }

    setAttachedFiles(prev => {
      const combined = [...prev, ...validFiles];
      if (combined.length > MAX_FILES) {
        setUploadError(t('inquiries.chat.tooManyFiles', { max: MAX_FILES }));
        return combined.slice(0, MAX_FILES);
      }
      return combined;
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [t]);

  const removeFile = (index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
    setUploadError(null);
  };

  const uploadFiles = async (files: File[]): Promise<number[]> => {
    if (files.length === 0) return [];
    const headers = { Authorization: `Bearer ${currentUser?.token}` };
    const docs = await Promise.all(
      files.map(async (f) => ({
        name: f.name,
        description: '',
        contentBase64: await fileToBase64(f),
      })),
    );
    const result = await postAuth<UploadedDoc[]>('documents/upload', headers, docs);
    return result.map(d => d.id);
  };

  const handleSend = async () => {
    if (!input.trim() && attachedFiles.length === 0) return;

    let docIds: number[] = [];
    if (attachedFiles.length > 0) {
      setIsUploading(true);
      setUploadError(null);
      try {
        docIds = await uploadFiles(attachedFiles);
      } catch {
        setUploadError(t('inquiries.chat.uploadFailed'));
        setIsUploading(false);
        return;
      }
      setIsUploading(false);
    }

    onSendMessage(input.trim(), isInternal, replyToId, docIds);
    setInput('');
    setReplyToId(null);
    setAttachedFiles([]);
    setUploadError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const replyToMessage = replyToId ? messages.find(m => m.id === replyToId) : null;
  const canSend = (input.trim() || attachedFiles.length > 0) && !isUploading;

  return (
    <div className={styles.container}>
      {/* ph-mask: שיחות הפניות הן טקסט חופשי על אנשים - ממוסך בהקלטות PostHog (HAT-1081). */}
      <div ref={scrollRef} className={`${styles.messagesArea} custom-scrollbar ph-mask`}>
        {/*
          * ⚠️ עמודה ממורכזת ברוחב מוגבל, ולא רוחב מלא. הפאנל רחב, בועה נצמדת
          * לכל קצה, ובאמצע נשאר שטח לבן שגורם לשרשור להיראות ריק - זה מה
          * שנראה על המסך בבדיקה. הקווים המקווקווים בצדדים הם בקשה מפורשת:
          * הם מסמנים את גבולות השיחה ומחזיקים את העין באמצע.
          */}
        <div className={styles.column}>
          {messages.map((msg, index) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              isManagement={isManagement}
              /*
               * הקיבוץ נחתך כאן ולא בתוך הבועה: רק כאן רואים את ההודעה
               * הקודמת. ראה startsGroup לכללים.
               */
              startsGroup={startsGroup(messages, index)}
              onReply={() => setReplyToId(msg.id)}
            />
          ))}
        </div>
        {isLoading && (
          <div className={styles.loadingCenter}>
            <div className={styles.loadingSpinner} role="status" aria-busy />
          </div>
        )}
      </div>

      <hr className={styles.divider} />

      {replyToMessage && (
        <div className={styles.replyPreview}>
          <ReplyIcon sx={{ fontSize: 16, color: 'var(--font-body-2)' }} />
          <span className={styles.replyPreviewText}>
            {replyToMessage.senderName}: {replyToMessage.content.substring(0, 80)}
          </span>
          <button className={styles.replyCloseBtn} onClick={() => setReplyToId(null)}>✕</button>
        </div>
      )}

      {attachedFiles.length > 0 && (
        <div className={styles.attachedFiles}>
          {attachedFiles.map((file, i) => (
            <div key={`${file.name}-${i}`} className={styles.fileChip}>
              <InsertDriveFileIcon sx={{ fontSize: 14 }} />
              <span className={styles.fileChipName}>{file.name}</span>
              <button className={styles.fileChipRemove} onClick={() => removeFile(i)}>
                <CloseIcon sx={{ fontSize: 12 }} />
              </button>
            </div>
          ))}
        </div>
      )}

      {uploadError && (
        <div className={styles.uploadError}>{uploadError}</div>
      )}

      <div className={styles.inputArea}>
        {isManagement && (
          <div className={isInternal ? styles.internalWrapActive : styles.internalWrap}>
            <button
              type="button"
              className={isInternal ? styles.internalToggleActive : styles.internalToggle}
              onClick={() => setIsInternal(prev => !prev)}
            >
              <span className={isInternal ? styles.switchOn : styles.switchOff}>
                <span className={styles.switchThumb} />
              </span>
              <LockIcon sx={{ fontSize: 14 }} />
              <span>{t('inquiries.chat.internalNoteLabel')}</span>
            </button>
            {isInternal && (
              <div className={styles.internalBanner}>
                {t('inquiries.chat.internalBanner')}
              </div>
            )}
          </div>
        )}
        <div className={styles.inputRow}>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('inquiries.chat.placeholder')}
            rows={1}
            disabled={(isClosed && !isManagement) || isUploading}
            className={isInternal ? styles.textInputInternal : styles.textInput}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className={styles.hiddenFileInput}
            onChange={handleFileSelect}
          />
          <button
            className={styles.iconBtn}
            title={t('inquiries.chat.attachFile')}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <AttachFileIcon sx={{ fontSize: 20 }} />
          </button>
          <button
            className={styles.sendBtn}
            onClick={handleSend}
            disabled={!canSend}
            title={t('inquiries.chat.send')}
          >
            {isUploading
              ? <CircularProgress size={18} sx={{ color: 'white' }} />
              : <SendIcon sx={{ fontSize: 18 }} />}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DocInfo { id: number; name: string; url: string }

function formatSystemMessage(text: string, statuses: string[]): { text: string; bold: boolean }[] {
  const highlights = new Set<string>();

  for (const s of statuses) {
    if (text.includes(s)) highlights.add(s);
  }

  const byNameMatch = text.match(/ע"י\s+(.+)$/);
  if (byNameMatch?.[1]) highlights.add(byNameMatch[1]);

  const assigneeMatch = text.match(/שויך ל(?:מחלקת )?(.+?)\s+ע"י/);
  if (assigneeMatch?.[1]) highlights.add(assigneeMatch[1]);

  if (highlights.size === 0) return [{ text, bold: false }];

  const escapedHighlights = Array.from(highlights)
    .sort((a, b) => b.length - a.length)
    .map(h => h.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const regex = new RegExp(`(${escapedHighlights.join('|')})`, 'g');

  const parts: { text: string; bold: boolean }[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ text: text.slice(lastIndex, match.index), bold: false });
    }
    parts.push({ text: match[1], bold: true });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push({ text: text.slice(lastIndex), bold: false });
  }
  return parts;
}

/*
 * אחרי כמה דקות רצף נשבר גם אם אותו אדם ממשיך לכתוב. שלוש דקות ולא שלושים:
 * בשרשור בין רכז למשרד הפער בין הודעות הוא שעות, ורצף שנמשך לאורך שעתיים
 * מסתיר מתי בדיוק נאמר מה.
 */
const GROUP_BREAK_MINUTES = 3;

function messageDate(message: InquiryMessageDtoIn): Date {
  return new Date(message.createdAt + (message.createdAt.endsWith('Z') ? '' : 'Z'));
}

/**
 * Does this message open a new group - the one that shows the avatar, name and time?
 *
 * A group breaks on a change of writer, a change of day, a gap of more than a few minutes, and
 * around anything that is not a plain message (system lines, internal notes). Consecutive
 * messages from the same person inside one group carry no header at all, which is what stops a
 * one-word reply from being three lines of chrome around one word.
 */
function startsGroup(messages: InquiryMessageDtoIn[], index: number): boolean {
  const current = messages[index];
  if (current.isSystemMessage) return true;

  const previous = messages[index - 1];
  if (!previous) return true;

  // A system line between two messages ends the run: the thread has moved on.
  if (previous.isSystemMessage) return true;

  if (previous.senderRole !== current.senderRole) return true;
  // An internal note is a different kind of thing, even from the same person.
  if (!!previous.isInternalNote !== !!current.isInternalNote) return true;
  /*
   * The name too, not just the role: two secretaries answering the same inquiry are both
   * 'Office', and merging them would attribute one's words to the other.
   */
  if (previous.senderName !== current.senderName) return true;

  const previousAt = messageDate(previous);
  const currentAt = messageDate(current);
  if (previousAt.toDateString() !== currentAt.toDateString()) return true;

  return currentAt.getTime() - previousAt.getTime() > GROUP_BREAK_MINUTES * 60 * 1000;
}

interface MessageBubbleProps {
  message: InquiryMessageDtoIn;
  isManagement: boolean;
  /** First of a run by this writer: shows the avatar, name and time. */
  startsGroup: boolean;
  onReply: () => void;
}

function MessageBubble({ message, isManagement, startsGroup: isGroupStart, onReply }: MessageBubbleProps) {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [docs, setDocs] = useState<DocInfo[] | null>(null);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const hasDocuments = message.documentIds && message.documentIds.length > 0;

  const fetchDocs = useCallback(async () => {
    if (!hasDocuments || docs || loadingDocs) return;
    setLoadingDocs(true);
    try {
      const headers = { Authorization: `Bearer ${currentUser?.token}` };
      const result = await postAuth<DocInfo[]>('documents/info', headers, message.documentIds);
      setDocs(result);
    } catch {
      setDocs([]);
    }
    setLoadingDocs(false);
  }, [hasDocuments, docs, loadingDocs, currentUser?.token, message.documentIds]);

  useEffect(() => {
    if (hasDocuments) fetchDocs();
  }, [hasDocuments, fetchDocs]);

  if (message.isSystemMessage) {
    const STATUS_KEYS = ['New', 'WaitingForOffice', 'WaitingForUser', 'AnswerReceived', 'Closed'] as const;
    const STATUS_MAP: Record<string, string> = {};
    for (const key of STATUS_KEYS) {
      STATUS_MAP[key] = t(`inquiries.status.${key.charAt(0).toLowerCase() + key.slice(1)}`);
    }
    let translated = message.content;
    for (const [eng, heb] of Object.entries(STATUS_MAP)) {
      translated = translated.replace(new RegExp(eng, 'g'), heb);
    }

    const translatedStatuses = Object.values(STATUS_MAP);
    const parts = formatSystemMessage(translated, translatedStatuses);

    return (
      <div className={styles.systemMessage}>
        <span className={styles.systemMessageText}>
          {parts.map((p, i) => p.bold
            ? <strong key={i} className={styles.systemHighlight}>{p.text}</strong>
            : <span key={i}>{p.text}</span>
          )}
        </span>
      </div>
    );
  }

  const isOffice = message.senderRole === SenderRole.OFFICE;
  const isMyMessage = isManagement ? isOffice : !isOffice;

  const avatarLetter = message.senderName?.charAt(0) || '?';
  const utcDate = new Date(message.createdAt + (message.createdAt.endsWith('Z') ? '' : 'Z'));
  const time = utcDate.toLocaleTimeString('he-IL', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Jerusalem',
  });

  const bubbleClass = message.isInternalNote
    ? styles.bubbleInternal
    : isMyMessage
      ? styles.bubbleMine
      : styles.bubbleOther;

  return (
    <div className={isGroupStart ? styles.groupStart : styles.groupItem}>
      {/*
        * עיגול · שם · שעה בשורה אחת מעל הרצף, פעם אחת לרצף - כמו בצ׳אט של
        * גוגל. קודם השם היה בתוך כל בועה והשעה בשורה מתחתיה, כלומר הודעה של
        * מילה אחת נראתה כמו שלוש שורות.
        */}
      {isGroupStart && (
        <div className={isMyMessage ? styles.groupHeaderMine : styles.groupHeader}>
          <span className={isOffice ? styles.avatarOffice : styles.avatarUser}>
            {avatarLetter}
          </span>
          <span className={styles.groupSender}>{message.senderName}</span>
          <span className={styles.groupTime}>{time}</span>
        </div>
      )}

      <div className={isMyMessage ? styles.bubbleRowMine : styles.bubbleRow}>
      <div className={bubbleClass}>
        {message.isInternalNote && (
          <div className={styles.internalLabel}>
            <LockIcon sx={{ fontSize: 12 }} />
            <span>{t('inquiries.chat.internalNote')}</span>
          </div>
        )}
        {message.replyToPreview && (
          <div className={styles.replyQuote}>
            <span className={styles.replyQuoteText}>{message.replyToPreview}</span>
          </div>
        )}
        {message.content && <div className={styles.messageContent}>{message.content}</div>}
        {hasDocuments && (
          <div className={styles.messageAttachmentsList}>
            {docs ? docs.map((doc) => (
              <div key={doc.id} className={styles.attachmentLink}>
                <a
                  href={doc.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.attachmentAnchor}
                >
                  <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                  <span className={styles.attachmentName}>{doc.name}</span>
                </a>
                <DownloadFileButton uri={doc.url} fileName={doc.name} className={styles.attachmentDownload} />
              </div>
            )) : (
              <div className={styles.attachmentLoading}>
                <InsertDriveFileIcon sx={{ fontSize: 14 }} />
                <span>{t('inquiries.chat.attachmentsCount', { count: message.documentIds.length })}</span>
              </div>
            )}
          </div>
        )}
        {/*
          * השעה עברה לכותרת הרצף. כאן היא נשארת רק כ-title, כדי שהודעה
          * שיושבת בתוך רצף עדיין תגלה את השעה שלה בריחוף.
          */}
        <button
          className={isMyMessage ? styles.replyBtnStart : styles.replyBtnEnd}
          onClick={onReply}
          title={time}
        >
          <ReplyIcon sx={{ fontSize: 14 }} />
        </button>
      </div>
      </div>
    </div>
  );
}
